import React, { useEffect, useMemo, useState } from "react";
import useTemplateStore from "../../store/useTemplateStore";
import { QuestionMarkCircleIcon } from "../icons/icons";
import Pagination from "./structurePagination";
import StructurePopover from "./structurePopover";

interface Template {
  id: string;
  smiles: string;
  iupacName: string;
  parent: string;
  imageFilename: string;
  molecularWeight?: number;
  casNumber?: string;
}
type TemplateCategory = "head" | "linker" | "tail1" | "tail2";

interface TemplatePanelProps {
  onTemplateSelect: (smiles: string) => void;
  selectedSmilesInKetcher: string | null;
  templateInsertMode: "append" | "replace";
  onTemplateInsertModeChange: (mode: "append" | "replace") => void;
}

const TemplatePanel: React.FC<TemplatePanelProps> = ({
  onTemplateSelect,
  selectedSmilesInKetcher,
  templateInsertMode,
  onTemplateInsertModeChange,
}) => {
  const [activeTab, setActiveTab] = useState<TemplateCategory>("head");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [templatesPerPage] = useState(12);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  const {
    TemplateData,
    fetchTemplateData,
    isLoading: isStoreLoading,
  } = useTemplateStore();

  const getTemplatesArray = (
    categoryData: Record<string, any> | undefined
  ): Template[] => {
    if (!categoryData) return [];
    return Object.entries(categoryData).map(([id, template]) => ({
      id,
      smiles: template.smiles,
      iupacName: template.iupacName,
      parent: template.parent,
      imageFilename: template.imageFilename,
      molecularWeight: template.molecularWeight,
      casNumber: template.casNumber,
    }));
  };

  const filterTemplates = (templates: Template[], term: string): Template[] => {
    if (!term || term.trim() === "") return templates;
    const lowerCaseTerm = term.toLowerCase();
    return templates.filter(
      (template) =>
        (template.iupacName || "").toLowerCase().includes(lowerCaseTerm) ||
        (template.parent || "").toLowerCase().includes(lowerCaseTerm)
    );
  };

  const { currentTemplates, totalPages, filteredTemplatesCount } =
    useMemo(() => {
      if (!TemplateData) {
        return {
          currentTemplates: [],
          totalPages: 0,
          filteredTemplatesCount: 0,
        };
      }

      const templatesForCurrentTab = getTemplatesArray(TemplateData[activeTab]);
      const filtered = filterTemplates(templatesForCurrentTab, searchTerm);
      const indexOfLastTemplate = currentPage * templatesPerPage;
      const indexOfFirstTemplate = indexOfLastTemplate - templatesPerPage;

      return {
        currentTemplates: filtered.slice(
          indexOfFirstTemplate,
          indexOfLastTemplate
        ),
        totalPages: Math.ceil(filtered.length / templatesPerPage),
        filteredTemplatesCount: filtered.length,
      };
    }, [TemplateData, activeTab, searchTerm, currentPage, templatesPerPage]);

  const handleTabClick = (tab: TemplateCategory) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    if (!TemplateData && !isStoreLoading) {
      fetchTemplateData();
    }
  }, [TemplateData, fetchTemplateData, isStoreLoading]);

  if (isStoreLoading && !TemplateData) {
    return (
      <div className="flex items-center justify-center h-64 md:w-[28rem] w-full">
        Loading templates...
      </div>
    );
  }

  return (
    <div className="relative md:w-[28rem] w-full p-2 flex flex-col h-full bg-gray-50">
      <h2 className="mb-3 text-lg font-semibold text-center text-gray-700">
        Ionizable Lipid Templates
      </h2>
      <div className="flex items-center px-4 mb-4 space-x-2">
        <input
          type="text"
          placeholder="Search IUPAC or parent"
          className="flex-grow px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setCurrentPage(1);
          }}
        />
        <div className="flex-shrink-0 flex items-center space-x-1.5">
          <div
            className="inline-flex items-center rounded-md shadow-sm"
            role="group"
          >
            <button
              type="button"
              onClick={() => onTemplateInsertModeChange("append")}
              className={`px-2.5 py-2 text-xs font-medium border border-gray-300 rounded-l-md focus:z-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${
                templateInsertMode === "append"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              title="Append: Adds template to current editor content."
            >
              Append
            </button>
            <button
              type="button"
              onClick={() => onTemplateInsertModeChange("replace")}
              className={`px-2.5 py-2 text-xs font-medium border-t border-b border-r border-gray-300 rounded-r-md focus:z-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${
                templateInsertMode === "replace"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              title="Replace: Clears editor and loads the new template."
            >
              Replace
            </button>
          </div>
          <div
            className="relative cursor-help p-0.5"
            onMouseEnter={() => setShowHelpTooltip(true)}
            onMouseLeave={() => setShowHelpTooltip(false)}
          >
            <QuestionMarkCircleIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
            {showHelpTooltip && (
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 w-max max-w-[250px] p-2 text-xs text-white bg-black rounded-md shadow-lg z-20">
                <p>
                  <strong>Append:</strong> Adds template to the current editor
                  content.
                </p>
                <p className="mt-1">
                  <strong>Replace:</strong> Clears editor and loads the new
                  template.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-center w-full px-4 mb-2">
        {(["head", "linker", "tail1", "tail2"] as TemplateCategory[]).map(
          (tab, index, arr) => (
            <button
              key={tab}
              className={`px-4 py-2 text-xs font-medium focus:outline-none w-full ${
                index === 0 ? "rounded-l-md" : ""
              } ${index === arr.length - 1 ? "rounded-r-md" : ""} ${
                activeTab === tab
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={() => handleTabClick(tab)}
            >
              {tab === "tail1" || tab === "tail2"
                ? `Tail ${tab.slice(-1)}`
                : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          )
        )}
      </div>
      <div className="flex flex-col flex-grow mx-4 mb-2 overflow-hidden">
        <div className="grid content-start flex-grow grid-cols-3 gap-3 p-1 overflow-y-auto">
          {currentTemplates.map((template) => (
            <button
              key={template.id}
              className={`p-1 text-sm rounded-lg border shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400 ${
                selectedSmilesInKetcher === template.smiles
                  ? "bg-blue-100 text-blue-700 border-blue-400 ring-2 ring-blue-400"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:border-gray-400"
              }`}
              onClick={() => onTemplateSelect(template.smiles)}
            >
              <StructurePopover
                id={`template-popover-${template.id}`}
                iupacName={template.iupacName}
                parent={template.parent}
                molecularWeight={template.molecularWeight}
                casNumber={template.casNumber}
              >
                <div className="flex items-center justify-center w-full h-28">
                  <img
                    src={`/template_images/${activeTab}/${template.imageFilename}`}
                    alt={template.iupacName || "Template structure"}
                    className="object-contain max-w-full max-h-full"
                    loading="lazy"
                  />
                </div>
              </StructurePopover>
            </button>
          ))}
          {currentTemplates.length === 0 && !isStoreLoading && (
            <p className="col-span-3 mt-4 text-center text-gray-500">
              No templates found matching your criteria.
            </p>
          )}
        </div>
        <div className="flex justify-center flex-shrink-0 pt-2 mt-auto">
          {filteredTemplatesCount > templatesPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePanel;
